import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import {
  logTemuPlatformJson,
  TEMU_PLATFORM_JSON_KINDS,
} from '@/temu-platform/utils/temuPlatformJsonConsole';
import { showToast } from '@/utils/toast';
import '@/styles/temu-platform/temu-product-ops.css';

function formatJson(value) {
  if (value == null) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function kindApiHint(kind) {
  const found = TEMU_PLATFORM_JSON_KINDS.find((item) => item.kind === kind);
  return found?.api || '';
}

/**
 * 最近一次「同步更新」收录的 Temu 平台原始 JSON（未改写）。
 *
 * 通用组件：页面只要传入 shopId + dumpKind 即可，适用于商品 / 店铺信息 / 库存 /
 * 营销 / 退货单 / 议价单等所有会打平台 OpenAPI 的模块。
 * 打开时会同时把该份 JSON 打到浏览器控制台，便于直接复制排查。
 */
export default function SyncPlatformJsonModal({
  open = false,
  shopId,
  dumpKind,
  title = fixedUi("平台 JSON"),
    hint = '最近一次拉取 TEMU 后台数据时平台返回的原始响应（含 success / errorCode / result，未改写）',
  onClose,
}) {
  const [dumpLoading, setDumpLoading] = useState(false);
  const [dumpJson, setDumpJson] = useState(null);
  const [dumpError, setDumpError] = useState('');

  const loadDump = useCallback(async ({ silent = false } = {}) => {
    if (!shopId || !dumpKind) return null;
    setDumpLoading(true);
    if (!silent) setDumpError('');
    try {
      const data = await temuPlatformAPI.fetchSyncPlatformDump(shopId, dumpKind);
      setDumpJson(data);
      setDumpError('');
      // 同步打印到控制台，方便复制 / 贴回问题单
      logTemuPlatformJson(dumpKind, data, { shopId, reason: '查看平台 JSON' });
      return data;
    } catch (e) {
      setDumpJson(null);
      if (!silent) setDumpError(e?.message || '读取同步 dump 失败（请先点「拉取 TEMU 后台数据」）');
      return null;
    } finally {
      setDumpLoading(false);
    }
  }, [shopId, dumpKind]);

  useEffect(() => {
    if (!open) return undefined;
    setDumpJson(null);
    setDumpError('');
    return undefined;
  }, [open, shopId, dumpKind]);

  useEffect(() => {
    if (!open) return undefined;
    if (!dumpJson && !dumpLoading && !dumpError) {
      loadDump();
    }
    return undefined;
  }, [open, dumpJson, dumpLoading, dumpError, loadDump]);

  if (!open) return null;

  const bodyText = dumpLoading || dumpError ? '' : formatJson(dumpJson);
  const emptyHint = dumpLoading
    ? fixedUi("加载同步 dump…")
    : (dumpError || fixedUi("暂无平台同步 JSON"));

  const copyCurrent = async () => {
    const text = bodyText || emptyHint;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      showToast(fixedUi("已复制"), 'success');
    } catch {
      showToast(fixedUi("复制失败"), 'error');
    }
  };

  const printToConsole = () => {
    if (!dumpJson) {
      showToast(fixedUi("暂无可打印的平台 JSON"), 'warning');
      return;
    }
    logTemuPlatformJson(dumpKind, dumpJson, { shopId, reason: '手动打印' });
    showToast(fixedUi("已打印到控制台（F12 查看）"), 'success');
  };

  const downloadDump = async () => {
    if (!shopId || !dumpKind) return;
    try {
      await temuPlatformAPI.downloadSyncPlatformDump(shopId, dumpKind);
      showToast(fixedUi("已开始下载"), 'success');
    } catch (e) {
      showToast(e?.message || fixedUi("下载失败"), 'error');
    }
  };

  const apiHint = kindApiHint(dumpKind);

  return createPortal(
    <div className="tpo-batch-modal tpo-cert-json-modal" role="presentation">
      <button
        type="button"
        className="tpo-batch-modal__backdrop"
        aria-label={fixedUi("关闭")}
        onClick={onClose}
      />
      <div
        className="tpo-batch-modal__panel tpo-cert-json-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpo-sync-json-title"
      >
        <div className="tpo-batch-modal__head">
          <div>
            <h2 id="tpo-sync-json-title">{title}</h2>
            <p className="tpo-batch-modal__hint" style={{ margin: '4px 0 0' }}>
              {hint}
            </p>
            {apiHint ? (
              <p className="tpo-batch-modal__hint" style={{ margin: '2px 0 0' }}>
                接口：{apiHint}
              </p>
            ) : null}
          </div>
          <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" onClick={onClose}>
            {fixedUi("关闭")}
          </button>
        </div>

        <div className="tpo-batch-modal__body tpo-cert-json-modal__body">
          {bodyText ? (
            <pre className="tpo-cert-json-modal__pre">{bodyText}</pre>
          ) : (
            <p className="tpo-batch-modal__hint">{emptyHint}</p>
          )}
        </div>

        <div className="tpo-batch-modal__foot">
          <button
            type="button"
            className="tpo-btn tpo-btn--secondary tpo-btn--sm"
            disabled={dumpLoading || !shopId}
            onClick={() => loadDump()}
          >
            {dumpLoading ? fixedUi("加载中…") : fixedUi("重新加载 dump")}
          </button>
          <button
            type="button"
            className="tpo-btn tpo-btn--secondary tpo-btn--sm"
            disabled={!dumpJson}
            onClick={printToConsole}
          >
            打印到控制台
          </button>
          <button
            type="button"
            className="tpo-btn tpo-btn--secondary tpo-btn--sm"
            disabled={!shopId}
            onClick={downloadDump}
          >
            {fixedUi("下载 dump")}
          </button>
          <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" onClick={copyCurrent}>
            {fixedUi("复制当前")}
          </button>
          <button type="button" className="tpo-btn tpo-btn--primary tpo-btn--sm" onClick={onClose}>
            {fixedUi("完成")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
